
"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, User, signOut, getIdToken } from "firebase/auth";
import { auth } from "@/lib/firebase";
import { usePathname, useRouter } from "next/navigation";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  logout: () => Promise<void>;
  setAuthHeader: (headers: Headers) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  logout: async () => {},
  setAuthHeader: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (loading) return;

    const isAuthPage = pathname === "/login" || pathname === "/signup";
    
    if (!user && !isAuthPage) {
      router.push("/login");
    } 
    else if (user && isAuthPage) {
      router.push("/dashboard");
    }
  }, [user, loading, pathname, router]);


  const logout = async () => {
    await signOut(auth);
    router.push('/login');
  };

  const setAuthHeader = async (headers: Headers) => {
    if (auth.currentUser) {
      const token = await getIdToken(auth.currentUser);
      headers.set('Authorization', `Bearer ${token}`);
    }
  };

  const value = {
    user,
    loading,
    logout,
    setAuthHeader,
  };

  if (loading) {
    return null; // or a global loading spinner
  }

  const isAuthPage = pathname === "/login" || pathname === "/signup";
  // Render children if the user is logged in and not on an auth page,
  // or if the user is not logged in and is on an auth page.
  const shouldRenderChildren = user ? !isAuthPage : isAuthPage;

  return (
    <AuthContext.Provider value={value}>
      {shouldRenderChildren ? children : null}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
